import * as L from 'leaflet';

import { PlayerClusterType } from '../models/player-cluster.model';
import { PlayerMarkerType } from '../models/player-marker.model';

export type DisplayedPlayerMarkers = Map<string, L.Marker>;
export type SectionPolyline = {
  coordinates: L.LatLngExpression[];
  color: string;
};

export type ClosestPolylinePosition = {
  sectionIndex: number;
  coordinateIndex: number;
};

export type SplitSectionsPolylines = {
  remaining: SectionPolyline[];
  travelled: SectionPolyline[];
};

export function fitPolylineBounds(map: L.Map, coordinates: L.LatLngExpression[]): void {
  if (coordinates.length === 0) return;

  map.fitBounds(L.polyline(coordinates).getBounds(), { padding: [24, 24] });
}

export function findClosestPolylinePosition(
  sections: SectionPolyline[],
  broadcastCoordinates: [number, number],
): ClosestPolylinePosition | null {
  const broadcastLatLng: L.LatLng = L.latLng(broadcastCoordinates[0], broadcastCoordinates[1]);
  let closestPosition: ClosestPolylinePosition | null = null;
  let closestDistance: number = Number.POSITIVE_INFINITY;

  sections.forEach((section: SectionPolyline, sectionIndex: number) => {
    (section.coordinates as L.LatLngTuple[]).forEach(
      (coordinate: L.LatLngTuple, coordinateIndex: number) => {
        const distance: number = broadcastLatLng.distanceTo(L.latLng(coordinate[0], coordinate[1]));

        if (distance < closestDistance) {
          closestDistance = distance;
          closestPosition = { sectionIndex, coordinateIndex };
        }
      },
    );
  });

  return closestPosition;
}

export function splitSectionsPolylines(
  sections: SectionPolyline[],
  broadcastCoordinates?: [number, number],
): SplitSectionsPolylines {
  if (!sections.length || !broadcastCoordinates) {
    return { remaining: sections, travelled: [] };
  }

  const closestPosition: ClosestPolylinePosition | null = findClosestPolylinePosition(
    sections,
    broadcastCoordinates,
  );

  if (!closestPosition) {
    return { remaining: sections, travelled: [] };
  }

  const travelledSections: SectionPolyline[] = [];
  const remainingSections: SectionPolyline[] = [];

  sections.forEach((section: SectionPolyline, sectionIndex: number) => {
    if (sectionIndex < closestPosition.sectionIndex) {
      travelledSections.push({
        coordinates: section.coordinates,
        color: `${section.color}dd`,
      });
      return;
    }

    if (sectionIndex > closestPosition.sectionIndex) {
      remainingSections.push(section);
      return;
    }

    const travelledCoordinates: L.LatLngExpression[] = section.coordinates.slice(
      0,
      closestPosition.coordinateIndex,
    );
    const remainingCoordinates: L.LatLngExpression[] = section.coordinates.slice(
      closestPosition.coordinateIndex,
    );

    if (travelledCoordinates.length) {
      travelledSections.push({
        coordinates: travelledCoordinates,
        color: `${section.color}dd`,
      });
    }

    if (remainingCoordinates.length) {
      remainingSections.push({
        coordinates: remainingCoordinates,
        color: section.color,
      });
    }
  });

  return { travelled: travelledSections, remaining: remainingSections };
}

export function buildPlayerClusters(
  map: L.Map,
  playersMarkers: PlayerMarkerType[],
  maxClusterDistance: number,
): PlayerClusterType[] {
  const clusters: PlayerClusterType[] = [];

  playersMarkers.forEach((playerMarker: PlayerMarkerType) => {
    if (playerMarker.isClient) {
      clusters.push({
        center: playerMarker.coordinates,
        players: [playerMarker],
      });
      return;
    }

    const playerPoint: L.Point = map.latLngToLayerPoint(playerMarker.coordinates);

    const existingCluster: PlayerClusterType | undefined = clusters.find(
      (cluster: PlayerClusterType) =>
        !cluster.players.some((player: PlayerMarkerType) => player.isClient) &&
        map.latLngToLayerPoint(cluster.center).distanceTo(playerPoint) <= maxClusterDistance,
    );

    if (!existingCluster) {
      clusters.push({
        center: playerMarker.coordinates,
        players: [playerMarker],
      });
      return;
    }

    existingCluster.players.push(playerMarker);
    existingCluster.center = getClusterCenter(existingCluster.players);
  });

  return clusters;
}

export function getClusterCenter(players: PlayerMarkerType[]): L.LatLng {
  const total: L.LatLngLiteral = players.reduce(
    (accumulator: L.LatLngLiteral, player: PlayerMarkerType) => ({
      lat: accumulator.lat + player.coordinates.lat,
      lng: accumulator.lng + player.coordinates.lng,
    }),
    { lat: 0, lng: 0 },
  );

  return L.latLng(total.lat / players.length, total.lng / players.length);
}

export function getClusterKey(cluster: PlayerClusterType): string {
  return cluster.players
    .map((player: PlayerMarkerType) => player.user.username)
    .sort()
    .join('|');
}

export function removeDisplayedPlayerMarker(
  map: L.Map,
  displayedPlayerMarkers: DisplayedPlayerMarkers,
  playerKey: string,
): void {
  const marker: L.Marker | undefined = displayedPlayerMarkers.get(playerKey);
  if (!marker) return;

  map.removeLayer(marker);
  displayedPlayerMarkers.delete(playerKey);
}

export function removeDisplayedPlayerMarkers(
  map: L.Map,
  displayedPlayerMarkers: DisplayedPlayerMarkers,
  playerKeys: string[],
): void {
  playerKeys.forEach((playerKey: string) =>
    removeDisplayedPlayerMarker(map, displayedPlayerMarkers, playerKey),
  );
}

export function removeDisplayedClusterMarker(
  map: L.Map,
  displayedClusterMarkers: DisplayedPlayerMarkers,
  clusterKey: string,
): void {
  const marker: L.Marker | undefined = displayedClusterMarkers.get(clusterKey);
  if (!marker) return;

  map.removeLayer(marker);
  displayedClusterMarkers.delete(clusterKey);
}

export function removeDisplayedClusterMarkersForPlayers(
  map: L.Map,
  displayedClusterMarkers: DisplayedPlayerMarkers,
  playerKeys: string[],
): void {
  const playersSet: Set<string> = new Set<string>(playerKeys);

  Array.from(displayedClusterMarkers.keys()).forEach((clusterKey: string) => {
    const clusterPlayers: string[] = clusterKey.split('|');
    if (clusterPlayers.some((playerKey: string) => playersSet.has(playerKey))) {
      removeDisplayedClusterMarker(map, displayedClusterMarkers, clusterKey);
    }
  });
}
